import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Eye,
  EyeOff,
  UserPlus,
  AlertCircle,
  Check,
  X,
  Mail,
  CheckCircle,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { RegisterFormData } from "../types";
import { getFirebaseAuthError } from "../utils/errorHandler";

const Register: React.FC = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState<RegisterFormData>({
    nome: "",
    cognome: "",
    cellulare: "",
    email: "",
    confirmEmail: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [acceptPolicy, setAcceptPolicy] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const { register, resendVerificationEmail } = useAuth();

  // Password validation requirements
  const passwordRequirements = useMemo(() => {
    const password = formData.password;
    return {
      minLength: password.length >= 12,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSymbol: /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\/~`]/.test(password),
    };
  }, [formData.password]);

  const isPasswordValid = useMemo(() => {
    return Object.values(passwordRequirements).every(Boolean);
  }, [passwordRequirements]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError("");
  };

  const validateForm = (): string | null => {
    if (formData.email !== formData.confirmEmail) {
      return t.register.errors.emailMismatch;
    }
    if (formData.password !== formData.confirmPassword) {
      return t.register.errors.passwordMismatch;
    }
    if (!isPasswordValid) {
      return t.register.errors.passwordRequirements;
    }
    if (!acceptPolicy) {
      return t.register.errors.acceptTerms;
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError("");

    try {
      await register(formData);
      setRegistrationSuccess(true);
    } catch (err) {
      const errorMessage = getFirebaseAuthError(err, t, "register");
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    try {
      await resendVerificationEmail();
      setResendSuccess(true);
    } catch (err) {
      console.error("Error resending verification email:", err);
    } finally {
      setResendLoading(false);
    }
  };

  // Show success message after registration
  if (registrationSuccess) {
    return (
      <div className="min-h-screen pt-24 pb-12 bg-gray-50">
        <div className="container-custom">
          <div className="max-w-lg mx-auto">
            <div className="card p-8 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
                <Mail className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="font-display text-2xl font-bold text-gray-900 mb-4">
                {t.register.verificationSent.title}
              </h1>
              <p className="text-gray-600 mb-4">
                {t.register.verificationSent.description}
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-amber-800 text-sm text-left">
                    {t.register.verificationSent.checkSpam}
                  </p>
                </div>
              </div>

              {resendSuccess ? (
                <div className="flex items-center justify-center space-x-2 text-green-600 mb-4">
                  <CheckCircle className="w-5 h-5" />
                  <span>{t.register.verificationSent.resendSuccess}</span>
                </div>
              ) : (
                <button
                  onClick={handleResendEmail}
                  disabled={resendLoading}
                  className="text-romanian-blue hover:underline font-medium mb-4 disabled:opacity-50"
                >
                  {resendLoading
                    ? t.register.verificationSent.resending
                    : t.register.verificationSent.resendEmail}
                </button>
              )}

              <div className="border-t pt-6 mt-6">
                <Link
                  to="/login"
                  className="btn-primary inline-flex items-center space-x-2"
                >
                  <span>{t.register.verificationSent.goToLogin}</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12 bg-gray-50">
      <div className="container-custom">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-romanian-blue rounded-xl mb-4">
              <UserPlus className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-display text-3xl font-bold text-romanian-blue">
              {t.register.title}
            </h1>
            <p className="text-gray-600 mt-2">{t.register.subtitle}</p>
          </div>

          {/* Form */}
          <div className="card p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Error Message */}
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <span className="text-red-700 text-sm">{error}</span>
                </div>
              )}

              {/* Personal Info */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-4 pb-2 border-b">
                  {t.register.personalInfo}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="nome" className="label">
                      {t.register.firstName} *
                    </label>
                    <input
                      type="text"
                      id="nome"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      required
                      className="input"
                      placeholder={t.register.yourFirstName}
                    />
                  </div>
                  <div>
                    <label htmlFor="cognome" className="label">
                      {t.register.lastName} *
                    </label>
                    <input
                      type="text"
                      id="cognome"
                      name="cognome"
                      value={formData.cognome}
                      onChange={handleChange}
                      required
                      className="input"
                      placeholder={t.register.yourLastName}
                    />
                  </div>
                  <div>
                    <label htmlFor="cellulare" className="label">
                      {t.register.phone} *
                    </label>
                    <input
                      type="tel"
                      id="cellulare"
                      name="cellulare"
                      value={formData.cellulare}
                      onChange={handleChange}
                      required
                      className="input"
                      placeholder="+39 393 581 7177"
                    />
                  </div>
                </div>
              </div>

              {/* Account */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-4 pb-2 border-b">
                  {t.register.accountInfo}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="label">
                      {t.register.email} *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="input"
                      placeholder="email@esempio.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="confirmEmail" className="label">
                      {t.register.confirmEmail} *
                    </label>
                    <input
                      type="email"
                      id="confirmEmail"
                      name="confirmEmail"
                      value={formData.confirmEmail}
                      onChange={handleChange}
                      required
                      className={`input ${formData.confirmEmail && formData.email !== formData.confirmEmail ? "input-error" : ""}`}
                      placeholder={t.register.confirmEmail}
                    />
                  </div>
                  <div className="relative md:col-span-2">
                    <label htmlFor="password" className="label">
                      {t.register.password} *
                    </label>
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      minLength={12}
                      className={`input pr-12 ${formData.password && !isPasswordValid ? "input-error" : ""}`}
                      placeholder={t.register.minChars}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                    {/* Password requirements checklist */}
                    {formData.password && (
                      <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-xs font-medium text-gray-700 mb-2">
                          {t.register.passwordRequirementsTitle}
                        </p>
                        <ul className="space-y-1">
                          <li
                            className={`flex items-center text-xs ${passwordRequirements.minLength ? "text-green-600" : "text-red-500"}`}
                          >
                            {passwordRequirements.minLength ? (
                              <Check className="w-3 h-3 mr-1" />
                            ) : (
                              <X className="w-3 h-3 mr-1" />
                            )}
                            {t.register.passwordReq.minLength}
                          </li>
                          <li
                            className={`flex items-center text-xs ${passwordRequirements.hasUppercase ? "text-green-600" : "text-red-500"}`}
                          >
                            {passwordRequirements.hasUppercase ? (
                              <Check className="w-3 h-3 mr-1" />
                            ) : (
                              <X className="w-3 h-3 mr-1" />
                            )}
                            {t.register.passwordReq.uppercase}
                          </li>
                          <li
                            className={`flex items-center text-xs ${passwordRequirements.hasLowercase ? "text-green-600" : "text-red-500"}`}
                          >
                            {passwordRequirements.hasLowercase ? (
                              <Check className="w-3 h-3 mr-1" />
                            ) : (
                              <X className="w-3 h-3 mr-1" />
                            )}
                            {t.register.passwordReq.lowercase}
                          </li>
                          <li
                            className={`flex items-center text-xs ${passwordRequirements.hasNumber ? "text-green-600" : "text-red-500"}`}
                          >
                            {passwordRequirements.hasNumber ? (
                              <Check className="w-3 h-3 mr-1" />
                            ) : (
                              <X className="w-3 h-3 mr-1" />
                            )}
                            {t.register.passwordReq.number}
                          </li>
                          <li
                            className={`flex items-center text-xs ${passwordRequirements.hasSymbol ? "text-green-600" : "text-red-500"}`}
                          >
                            {passwordRequirements.hasSymbol ? (
                              <Check className="w-3 h-3 mr-1" />
                            ) : (
                              <X className="w-3 h-3 mr-1" />
                            )}
                            {t.register.passwordReq.symbol}
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>
                  <div className="relative md:col-span-2">
                    <label htmlFor="confirmPassword" className="label">
                      {t.register.confirmPassword} *
                    </label>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      minLength={12}
                      className={`input pr-12 ${formData.confirmPassword && formData.password !== formData.confirmPassword ? "input-error" : ""}`}
                      placeholder={t.register.confirmPassword}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                    {formData.confirmPassword &&
                      formData.password !== formData.confirmPassword && (
                        <p className="text-xs text-red-500 mt-1">
                          {t.register.errors.passwordMismatch}
                        </p>
                      )}
                  </div>
                </div>
              </div>

              {/* Accept Privacy & Cookie Policy */}
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="acceptPolicy"
                  checked={acceptPolicy}
                  onChange={(e) => setAcceptPolicy(e.target.checked)}
                  className="mt-1 h-4 w-4 text-romanian-blue border-gray-300 rounded focus:ring-romanian-blue cursor-pointer"
                />
                <label
                  htmlFor="acceptPolicy"
                  className="text-sm text-gray-600 cursor-pointer"
                >
                  {t.register.termsAccept}{" "}
                  <Link
                    to="/privacy-policy"
                    target="_blank"
                    className="text-romanian-blue font-semibold hover:underline"
                  >
                    {t.register.privacyLink}
                  </Link>{" "}
                  {t.register.termsAnd}{" "}
                  <Link
                    to="/cookie-policy"
                    target="_blank"
                    className="text-romanian-blue font-semibold hover:underline"
                  >
                    Cookie Policy
                  </Link>
                </label>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center space-x-2"
              >
                {loading ? (
                  <>
                    <div className="spinner w-5 h-5 border-2 border-white/30 border-t-white" />
                    <span>{t.register.registering}</span>
                  </>
                ) : (
                  <>
                    <UserPlus className="w-5 h-5" />
                    <span>{t.register.registerButton}</span>
                  </>
                )}
              </button>

              {/* Login Link */}
              <p className="text-center text-gray-600">
                {t.register.haveAccount}{" "}
                <Link
                  to="/login"
                  className="text-romanian-blue font-semibold hover:underline"
                >
                  {t.register.loginLink}
                </Link>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
